import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Badge, Box, Checkbox, Chip, FormControlLabel, MenuItem, Popover, Stack,
    Table, TableBody, TableCell, TableHead, TableRow,
    TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import ActionButton from "../../../component/common/ActionButton";
import LoadingDots from "../../../component/common/LoadingDots";
import SearchField from "../../../component/common/SearchField";
import { workRequestApi } from "../api/workRequestApi";
import { formatDottedDate, formatDottedDateTimeMinute } from "../../../util/dateUtils";
import { STATUS_ORDER, statusMeta } from "./workRequestConstants";
import { ds } from "../../../styles/designSystem";
import dayjs from "dayjs";

const C = ds.colors;

/** 진행 여부 기본값 — 끝난 일이 목록을 채우면 할 일이 안 보인다. */
const OPEN_ONLY = "__OPEN__";
const EMPTY_FILTERS = {
    status: OPEN_ONLY, dept: "", person: "",
    createdFrom: "", createdTo: "", dueFrom: "", dueTo: "",
    // 기한 기간을 걸면 기한 없는 건은 기본적으로 빠진다 — 기간을 지정했는데 날짜 없는 건이
    // 섞여 나오면 결과를 믿기 어렵다. 필요하면 이 체크로 함께 본다.
    dueIncludeNone: false,
};

/** 기한이 지났거나 3일 이내면 강조 — 완료 건은 대상 아님. */
function dueTone(dueDate, status) {
    if (!dueDate || status === "COMPLETED") return null;
    const days = dayjs(dueDate).startOf("day").diff(dayjs().startOf("day"), "day");
    if (days < 0) return { color: "#b91c1c", label: `${Math.abs(days)}일 지남` };
    if (days <= 3) return { color: "#b45309", label: days === 0 ? "오늘 마감" : `${days}일 남음` };
    return null;
}

/**
 * 과업 요청서 목록 — <b>표</b>로 어느 팀에 무슨 일을 언제 맡겼고 지금 어디까지 왔는지 한 줄에 보인다.
 *
 * <p>카드로 두면 한 화면에 대여섯 건밖에 안 들어와 "내가 뭘 걸어놨는지" 를 훑을 수가 없다.
 * 표로 바꾸고 열을 팀·업무·진행·요청일·기한으로 고정했다.
 *
 * <p>구분은 <b>요청한 업무 / 배정된 업무</b> 둘뿐이다 — '전체' 는 남의 팀 건까지 섞여
 * 정작 내 일이 묻힌다. 완료 건은 기본으로 감춘다(끝난 일이 목록을 채우면 할 일이 안 보인다).
 *
 * <p>팀·상태 드롭다운은 없앴다. 열에 이미 다 보이고, 건수가 많지 않아 <b>검색 한 칸</b>이면 충분하다.
 */
/** 비어 있지 않은 필터만 센다 — 버튼 배지로 "지금 몇 개가 걸려 있는지" 를 보여주기 위해. */
const countActive = (f) => Object.entries(f)
    .filter(([k, v]) => v && !(k === "status" && v === OPEN_ONLY)).length;

export default function WorkRequestBoard({ onOpen, onCompose, reloadKey }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scope, setScope] = useState("mine");        // mine | assigned
    const [keyword, setKeyword] = useState("");
    const [filterAnchor, setFilterAnchor] = useState(null);

    /**
     * 필터를 <b>탭별로 따로</b> 보관한다.
     *
     * <p>두 탭은 같은 열이라도 뜻이 다르다 — '담당자' 열이 요청한 업무에선 담당자, 배정된 업무에선
     * 요청자다. 필터를 공유하면 탭을 옮기는 순간 "박주현" 이 다른 사람을 가리키게 되어
     * 조용히 0건이 뜬다. 따로 두면 각 탭이 자기 조건을 기억하고, 돌아왔을 때 그대로 복원된다.
     */
    const [filtersByScope, setFiltersByScope] = useState({
        mine: { ...EMPTY_FILTERS },
        assigned: { ...EMPTY_FILTERS },
    });
    const filters = filtersByScope[scope];
    const setFilters = (next) => setFiltersByScope(m => ({
        ...m, [scope]: typeof next === "function" ? next(m[scope]) : next,
    }));

    const load = useCallback(() => {
        setLoading(true);
        workRequestApi.getList(scope === "mine" ? { mine: true } : { assigned: true })
            .then(list => setRows(Array.isArray(list) ? list : []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [scope]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const doneCount = useMemo(() => rows.filter(r => r.status === "COMPLETED").length, [rows]);
    const activeCount = countActive(filters);

    /** 선택지는 <b>실제로 목록에 있는 값</b>에서 뽑는다 — 고르면 0건인 옵션을 주지 않기 위해. */
    const { deptOptions, personOptions } = useMemo(() => {
        const depts = new Set();
        const people = new Set();
        for (const r of rows) {
            if (r.targetDeptName) depts.add(r.targetDeptName);
            const person = scope === "mine" ? r.assigneeName : r.requesterName;
            if (person) people.add(person);
        }
        return {
            deptOptions: [...depts].sort(),
            personOptions: [...people].sort(),
        };
    }, [rows, scope]);

    const visible = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        const inRange = (value, from, to, includeNone = false) => {
            if (!from && !to) return true;            // 기간을 안 걸었으면 전부 통과
            if (!value) return includeNone;           // 날짜 없는 건 — 체크했을 때만 포함
            const d = dayjs(value);
            if (from && d.isBefore(dayjs(from), "day")) return false;
            if (to && d.isAfter(dayjs(to), "day")) return false;
            return true;
        };

        return rows.filter(r => {
            if (filters.status === OPEN_ONLY) { if (r.status === "COMPLETED") return false; }
            else if (filters.status && r.status !== filters.status) return false;

            if (filters.dept && r.targetDeptName !== filters.dept) return false;

            const person = scope === "mine" ? r.assigneeName : r.requesterName;
            if (filters.person && person !== filters.person) return false;

            if (!inRange(r.createdAt, filters.createdFrom, filters.createdTo)) return false;
            if (!inRange(r.dueDate, filters.dueFrom, filters.dueTo, filters.dueIncludeNone)) return false;

            if (q && ![r.title, r.targetDeptName, r.requestTypeName, r.requesterName, r.assigneeName]
                .some(v => (v || "").toLowerCase().includes(q))) return false;

            return true;
        });
    }, [rows, keyword, filters, scope]);

    const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <ToggleButtonGroup
                    exclusive size="small" value={scope}
                    onChange={(_, v) => v && setScope(v)}
                    sx={{
                        flexShrink: 0,
                        "& .MuiToggleButton-root": { fontSize: 12, textTransform: "none", px: 1.75, py: 0.5 },
                    }}
                >
                    <ToggleButton value="mine">요청한 업무</ToggleButton>
                    <ToggleButton value="assigned">배정된 업무</ToggleButton>
                </ToggleButtonGroup>

                {/* 왼쪽은 '무엇을 보는가'(탭), 오른쪽은 '무엇을 하는가'(검색·필터·작성)로 갈라 둔다. */}
                <Box sx={{ flex: 1, minWidth: 0 }} />

                <SearchField
                    value={keyword}
                    onChange={setKeyword}
                    placeholder="업무·팀·담당자 검색"
                    width={240}
                    sx={{ flexShrink: 0 }}
                />

                {/* 필터는 버튼 뒤로 숨긴다 — 항상 펼쳐두면 드롭다운 대여섯 개가 화면을 먹는다.
                    지금 몇 개가 걸려 있는지는 배지로 보여줘, 열어보지 않아도 알 수 있게. */}
                <Badge badgeContent={activeCount} color="primary"
                       sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}>
                    <ActionButton
                        variant="secondary" size="small"
                        startIcon={<FilterAltRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={(e) => setFilterAnchor(e.currentTarget)}
                        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                    >
                        필터
                    </ActionButton>
                </Badge>

                <ActionButton
                    variant="primary" size="small"
                    startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
                    onClick={onCompose}
                    sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                    요청 작성
                </ActionButton>
            </Stack>

            <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { p: 2, width: 320, borderRadius: 2 } }}
            >
                <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                        필터
                    </Typography>
                    <ActionButton variant="secondary" size="small" label="초기화" startIcon={false}
                                  onClick={() => setFilters({ ...EMPTY_FILTERS })} />
                </Stack>

                <Stack spacing={1.75}>
                    <FilterField label="진행 여부">
                        <TextField select fullWidth size="small" value={filters.status}
                                   onChange={(e) => setFilter("status", e.target.value)}
                                   SelectProps={{ displayEmpty: true }}>
                            <MenuItem value={OPEN_ONLY} sx={{ fontSize: 13 }}>진행 중 (완료 제외)</MenuItem>
                            <MenuItem value="" sx={{ fontSize: 13 }}>
                                전체{doneCount ? ` (완료 ${doneCount})` : ""}
                            </MenuItem>
                            {STATUS_ORDER.map(s => (
                                <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{statusMeta(s).label}</MenuItem>
                            ))}
                        </TextField>
                    </FilterField>

                    <FilterField label="요청 팀">
                        <TextField select fullWidth size="small" value={filters.dept}
                                   onChange={(e) => setFilter("dept", e.target.value)}
                                   SelectProps={{ displayEmpty: true }}>
                            <MenuItem value="" sx={{ fontSize: 13 }}>전체</MenuItem>
                            {deptOptions.map(d => (
                                <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>{d}</MenuItem>
                            ))}
                        </TextField>
                    </FilterField>

                    <FilterField label={scope === "mine" ? "담당자" : "요청자"}>
                        <TextField select fullWidth size="small" value={filters.person}
                                   onChange={(e) => setFilter("person", e.target.value)}
                                   SelectProps={{ displayEmpty: true }}>
                            <MenuItem value="" sx={{ fontSize: 13 }}>전체</MenuItem>
                            {personOptions.map(p => (
                                <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>
                            ))}
                        </TextField>
                    </FilterField>

                    <FilterField label="요청일">
                        <DateRange
                            from={filters.createdFrom} to={filters.createdTo}
                            onFrom={(v) => setFilter("createdFrom", v)}
                            onTo={(v) => setFilter("createdTo", v)}
                        />
                    </FilterField>

                    <FilterField label="기한">
                        <DateRange
                            from={filters.dueFrom} to={filters.dueTo}
                            onFrom={(v) => setFilter("dueFrom", v)}
                            onTo={(v) => setFilter("dueTo", v)}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={filters.dueIncludeNone}
                                    onChange={(e) => setFilter("dueIncludeNone", e.target.checked)}
                                />
                            }
                            label="기한 없는 요청도 포함"
                            sx={{ mt: 0.25, "& .MuiFormControlLabel-label": { fontSize: 11.5, color: C.textSecondary } }}
                        />
                    </FilterField>
                </Stack>
            </Popover>

            {loading && <LoadingDots />}

            {!loading && visible.length === 0 && (
                <Box sx={{
                    p: 4, textAlign: "center", borderRadius: 2,
                    border: `1px dashed ${C.border}`, bgcolor: "#fafbfc",
                }}>
                    <Typography sx={{ fontSize: 13, color: C.textMuted, mb: 1.5 }}>
                        {keyword.trim()
                            ? "검색 결과가 없습니다."
                            : scope === "mine"
                                ? "내가 요청한 업무가 없습니다."
                                : "나에게 배정된 업무가 없습니다."}
                    </Typography>
                    {!keyword.trim() && scope === "mine" && (
                        <ActionButton variant="primary" size="small" onClick={onCompose}>첫 요청 작성하기</ActionButton>
                    )}
                </Box>
            )}

            {!loading && visible.length > 0 && (
                <Box sx={{ ...ds.card, overflowX: "auto" }}>
                    <Table sx={ds.tableSx} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 110 }}>진행</TableCell>
                                <TableCell align="left">업무</TableCell>
                                <TableCell sx={{ width: 130 }}>요청 팀</TableCell>
                                <TableCell sx={{ width: 100 }}>
                                    {scope === "mine" ? "담당자" : "요청자"}
                                </TableCell>
                                <TableCell sx={{ width: 120 }}>요청일</TableCell>
                                <TableCell sx={{ width: 140 }}>기한</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map(r => {
                                const meta = statusMeta(r.status);
                                const due = dueTone(r.dueDate, r.status);
                                return (
                                    <TableRow
                                        key={r.id}
                                        hover
                                        onClick={() => onOpen(r.id)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                                <Chip label={meta.label} size="small" sx={{
                                                    height: 20, fontSize: 11, fontWeight: 700,
                                                    bgcolor: meta.bg, color: meta.fg, border: `1px solid ${meta.border}`,
                                                }} />
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted }}>
                                                    v{r.currentVersion}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
                                                {r.title}
                                            </Typography>
                                            <Typography noWrap sx={{ fontSize: 11, color: C.textMuted }}>
                                                {r.requestTypeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{r.targetDeptName || "-"}</TableCell>
                                        <TableCell>{scope === "mine" ? r.assigneeName : r.requesterName}</TableCell>
                                        <TableCell sx={{ color: C.textSecondary }}>
                                            {formatDottedDate(r.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            {r.dueDate ? (
                                                <>
                                                    <Typography sx={{
                                                        fontSize: 12.5,
                                                        color: due?.color || C.textSecondary,
                                                        fontWeight: due ? 700 : 400,
                                                    }}>
                                                        {formatDottedDate(r.dueDate)}
                                                    </Typography>
                                                    {due && (
                                                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: due.color }}>
                                                            {due.label}
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>-</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {!loading && visible.length > 0 && (
                <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1, textAlign: "right" }}>
                    {visible.length}건
                    {filters.status === OPEN_ONLY && doneCount > 0 ? ` · 완료 ${doneCount}건 숨김` : ""}
                    {rows[0]?.updatedAt ? ` · 최근 갱신 ${formatDottedDateTimeMinute(rows[0].updatedAt)}` : ""}
                </Typography>
            )}
        </Box>
    );
}

function FilterField({ label, children }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.textSecondary, mb: 0.5 }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

/** 기간 입력 — 한쪽만 채워도 된다(그 날짜 이후/이전으로 동작). */
function DateRange({ from, to, onFrom, onTo }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <TextField type="date" size="small" fullWidth value={from}
                       onChange={(e) => onFrom(e.target.value)}
                       inputProps={{ style: { fontSize: 12.5 } }} />
            <Typography sx={{ fontSize: 12, color: C.textMuted }}>~</Typography>
            <TextField type="date" size="small" fullWidth value={to}
                       onChange={(e) => onTo(e.target.value)}
                       inputProps={{ style: { fontSize: 12.5 } }} />
        </Stack>
    );
}
